import { Box, Typography } from '@mui/material';
import { motion } from 'framer-motion';
// Ensure this path is correct based on your file structure
import BioNeuralBackground from '../homepage/components/NeuralBackground';

import { useTheme } from '@mui/material/styles';
import { tokens } from '../../theme';

// --- SUB-COMPONENT: The DNA Helix Animation ---
const DNALoader = () => {
  // We create 8 "base pairs" (rows) for the helix
  const pairs = Array.from({ length: 8 });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.8 }}>
      {pairs.map((_, i) => (
        <Box key={i} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* Left Strand Node */}
          <Box
            component={motion.div}
            animate={{
              x: [10, -10, 10], // Move Horizontal
              opacity: [1, 0.5, 1], // Fade when "behind"
              scale: [1, 0.8, 1],   // Shrink when "behind"
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 0.15, // Stagger delays to create the spiral shape
            }}
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: '#06b6d4', // Cyan
              boxShadow: '0 0 8px rgba(6, 182, 212, 0.8)',
            }}
          />
          
          {/* Connecting Hydrogen Bond (The Line) */}
          <Box
             component={motion.div}
             animate={{ opacity: [0.2, 0.5, 0.2], width: [0, 20, 0] }}
             transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: i * 0.15 }}
             sx={{ height: '1px', bgcolor: 'rgba(6, 182, 212, 0.3)' }}
          />

          {/* Right Strand Node */}
          <Box
            component={motion.div}
            animate={{
              x: [-10, 10, -10], // Move Opposite to Left Node
              opacity: [0.5, 1, 0.5],
              scale: [0.8, 1, 0.8],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 0.15,
            }}
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: '#ffffff', // White
              boxShadow: '0 0 8px rgba(255, 255, 255, 0.5)',
            }}
          />
        </Box>
      ))}
    </Box>
  );
};

// --- MAIN COMPONENT ---
const AuthLoading = () => {
    const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${colors.primary[600]} 0%, #0f172a 100%)`, // Darker, richer depth
        position: 'relative',
        overflow: 'hidden',
        color: '#fff',
      }}
    >
      <Box 
              sx={{
                position: "absolute",
                top: 0, left: 0, right: 0, bottom: 0,
                zIndex: 0,
                // Create the dot pattern
                backgroundImage: "radial-gradient(rgba(255, 255, 255, 0.15) 1px, transparent 1px)",
                backgroundSize: "30px 30px", // Spacing between dots
                // Mask it so it fades out at the edges (Vignette)
                maskImage: "radial-gradient(circle at center, black 40%, transparent 100%)",
                WebkitMaskImage: "radial-gradient(circle at center, black 40%, transparent 100%)",
                pointerEvents: "none"
              }}
            />
      {/* 1. Background Animation */}
      <BioNeuralBackground transparent />

      <Box
        component={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        sx={{
          position: 'relative',
          zIndex: 10, // Ensure it sits on top of the canvas
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 5,
        }}
      >
        {/* 2. The Bio-Scanner Container */}
        <Box sx={{ position: 'relative', width: 140, height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            
            {/* Outer Scanner Ring (Rotating dashed line) */}
            <Box
                component={motion.div}
                animate={{ rotate: 360 }}
                transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
                sx={{
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    border: '1px dashed rgba(6, 182, 212, 0.3)',
                }}
            />

            {/* Inner "Focus" Ring (Pulsing) */}
            <Box
                component={motion.div}
                animate={{ scale: [0.95, 1.05, 0.95], opacity: [0.3, 0.6, 0.3] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                sx={{
                    position: 'absolute',
                    width: '85%',
                    height: '85%',
                    borderRadius: '50%',
                    border: '1px solid rgba(6, 182, 212, 0.2)',
                    background: 'radial-gradient(circle, rgba(6,182,212,0.1) 0%, transparent 70%)',
                }}
            />
            
            {/* The Central DNA Helix Animation */}
            <DNALoader />
            
        </Box>

        {/* 3. Status Text */}
        <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography
            variant="h6"
            component={motion.div}
            animate={{ opacity: [0.7, 1, 0.7] }}
            transition={{ duration: 2, repeat: Infinity }}
            sx={{
              fontWeight: 500,
              color: '#fff',
              letterSpacing: '1px',
              textTransform: 'uppercase',
              fontSize: '0.9rem'
            }}
          >
            Establishing Secure Uplink
          </Typography>
          
          {/* Animated "Terminal" Subtext */}
          <Typography
            variant="caption"
            sx={{
              color: '#06b6d4',
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1
            }}
          >
            <motion.span
                animate={{ opacity: [0, 1, 0] }}
                transition={{ duration: 0.1, repeat: Infinity, repeatDelay: 0.1 }}
            >
                ▋
            </motion.span>
            VERIFYING BIOMETRIC HASH...
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default AuthLoading;